import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../../../contexts/AuthContext';
import { PersonService } from '../../../../services/personService';
import { ValidationService } from '../../../../services/validationService';
import { BasicInfoForm } from './BasicInfoForm';
import { DatesForm } from './DatesForm';
import { LocationsForm } from './LocationsForm';
import { ProfessionalForm } from './ProfessionalForm';
import { BiographyForm } from './BiographyForm';
import { PersonalDetailsForm } from './PersonalDetailsForm';
import type { Person, PersonFormData } from '../../../../types';
import type { PermissionFlags } from '../PersonPage.types';

interface PersonEditDialogProps {
  open: boolean;
  person: Person;
  onClose: () => void;
  onSave: () => void;
  permissions: PermissionFlags;
}

/**
 * PersonEditDialog - Modal dialog for editing person information
 *
 * Features:
 * - 6 tabbed form sections
 * - Form validation
 * - Change tracking on save
 * - Role-based field restrictions
 */
export const PersonEditDialog: React.FC<PersonEditDialogProps> = ({
  open,
  person,
  onClose,
  onSave,
  permissions
}) => {
  const { authState } = useAuth();
  const currentUser = authState.user;

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Partial<Person>>(person);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Reset form data when person changes or dialog opens
  useEffect(() => {
    if (open) {
      setFormData(person);
      setErrors({});
      setGeneralError(null);
      setActiveTab(0);
    }
  }, [open, person]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Clear general error
    if (generalError) {
      setGeneralError(null);
    }
  };

  const convertToFormData = (data: Partial<Person>): PersonFormData => {
    return {
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      maidenName: data.maidenName || '',
      nicknames: Array.isArray(data.nicknames) ? data.nicknames.join(', ') : '',
      middleName: data.middleName || '',
      suffix: data.suffix || '',
      gender: data.gender || 'male',
      birthDate: data.birthDate || '',
      deathDate: data.deathDate || '',
      isDeceased: data.isDeceased || false,
      birthPlace: data.birthPlace || { city: '', state: '', country: '' },
      deathPlace: data.deathPlace || { city: '', state: '', country: '' },
      currentLocation: data.currentLocation || { city: '', state: '', country: '' },
      occupation: data.occupation || '',
      education: data.education || '',
      biography: data.biography || '',
      personalNotes: data.personalNotes || '',
      isPrivate: data.isPrivate || false
    };
  };

  const handleSave = async () => {
    if (!currentUser) {
      setGeneralError('You must be logged in to edit profiles');
      return;
    }

    if (!permissions.canEdit) {
      setGeneralError('You do not have permission to edit profiles');
      return;
    }

    setSaving(true);
    setGeneralError(null);

    try {
      // Convert to form data for validation
      const formDataForValidation = convertToFormData(formData);

      // Validate using ValidationService
      const validation = ValidationService.validatePersonForm(formDataForValidation);

      if (!validation.isValid) {
        // Set errors from validation
        const validationErrors: Record<string, string> = {};
        Object.entries(validation.errors).forEach(([field, message]) => {
          validationErrors[field] = message;
        });
        setErrors(validationErrors);
        setSaving(false);
        return;
      }

      // Calculate changed fields only
      const changedFields: Partial<Person> = {};
      Object.keys(formData).forEach(key => {
        const typedKey = key as keyof Person;
        if (JSON.stringify(formData[typedKey]) !== JSON.stringify(person[typedKey])) {
          (changedFields as any)[typedKey] = formData[typedKey];
        }
      });

      // Only update if there are actual changes
      if (Object.keys(changedFields).length === 0) {
        onClose();
        return;
      }

      // Save changes (PersonService.updatePerson includes change logging from Phase 0)
      await PersonService.updatePerson(
        person.id,
        changedFields,
        currentUser.uid,
        currentUser.displayName || 'Unknown User',
        currentUser.role === 'admin'
      );

      // Call onSave callback to refresh UI
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Save error:', err);
      setGeneralError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const disabled = !permissions.canEdit || saving;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={saving}
    >
      <DialogTitle>Edit Profile</DialogTitle>
      <DialogContent>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Basic Info" />
          <Tab label="Dates" />
          <Tab label="Locations" />
          <Tab label="Professional" />
          <Tab label="Biography" />
          <Tab label="Personal" />
        </Tabs>

        {/* General Error */}
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}

        {/* Tab Panels */}
        {activeTab === 0 && (
          <BasicInfoForm
            data={formData}
            errors={errors}
            onChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {activeTab === 1 && (
          <DatesForm
            data={formData}
            errors={errors}
            onChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {activeTab === 2 && (
          <LocationsForm
            data={formData}
            errors={errors}
            onChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {activeTab === 3 && (
          <ProfessionalForm
            data={formData}
            errors={errors}
            onChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {activeTab === 4 && (
          <BiographyForm
            data={formData}
            errors={errors}
            onChange={handleFieldChange}
            disabled={disabled}
          />
        )}
        {activeTab === 5 && (
          <PersonalDetailsForm
            data={formData}
            errors={errors}
            onChange={handleFieldChange}
            disabled={disabled}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={disabled}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
